import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Toggle } from '../ui/Toggle';
interface BundleProduct {
  productId: string;
  productName: string;
  requireSizeSelection: boolean;
}
interface BundleFormData {
  id?: string;
  name: string;
  price: string;
  products: BundleProduct[];
}
interface CreateBundleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (bundle: BundleFormData) => void;
  availableProducts: Array<{
    id: string;
    name: string;
  }>;
  editBundle?: BundleFormData | null;
}
export function CreateBundleModal({
  isOpen,
  onClose,
  onSave,
  availableProducts,
  editBundle
}: CreateBundleModalProps) {
  const [formData, setFormData] = useState<BundleFormData>(editBundle || {
    name: '',
    price: '',
    products: []
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Bundle name is required';
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }
    if (formData.products.length === 0) {
      newErrors.products = 'Select at least one product';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = () => {
    if (!validate()) return;
    onSave(formData);
    onClose();
  };
  const toggleProduct = (product: {
    id: string;
    name: string;
  }) => {
    setFormData(prev => {
      const exists = prev.products.find(p => p.productId === product.id);
      if (exists) {
        return {
          ...prev,
          products: prev.products.filter(p => p.productId !== product.id)
        };
      } else {
        return {
          ...prev,
          products: [...prev.products, {
            productId: product.id,
            productName: product.name,
            requireSizeSelection: true
          }]
        };
      }
    });
  };
  const updateProductSizeRequirement = (productId: string, requireSize: boolean) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map(p => p.productId === productId ? {
        ...p,
        requireSizeSelection: requireSize
      } : p)
    }));
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={editBundle ? 'Edit Bundle' : 'Create Bundle'} size="lg" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-410">
            Cancel
          </Button>
          <Button onClick={handleSave} data-id="element-411">
            {editBundle ? 'Update Bundle' : 'Save Bundle'}
          </Button>
        </>} data-id="element-409">
      <div className="space-y-6" data-id="element-412">
        <Input label="Bundle Name *" value={formData.name} onChange={e => setFormData(prev => ({
        ...prev,
        name: e.target.value
      }))} error={errors.name} placeholder="e.g., Player Pack" data-id="element-413" />

        <Input label="Bundle Price *" type="number" step="0.01" value={formData.price} onChange={e => setFormData(prev => ({
        ...prev,
        price: e.target.value
      }))} error={errors.price} placeholder="79.99" helperText="Set a discounted price for the bundle" data-id="element-414" />

        <div data-id="element-415">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-416">
            Included Products *{' '}
            {errors.products && <span className="text-red-600 text-xs ml-2" data-id="element-417">
                {errors.products}
              </span>}
          </label>

          {availableProducts.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200" data-id="element-418">
              <p className="text-sm text-gray-500" data-id="element-419">
                No products available. Add products first.
              </p>
            </div> : <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3" data-id="element-420">
              {availableProducts.map(product => {
            const isSelected = formData.products.some(p => p.productId === product.id);
            const bundleProduct = formData.products.find(p => p.productId === product.id);
            return <div key={product.id} className="space-y-2" data-id="element-421">
                    <label className="flex items-center p-2 hover:bg-gray-50 rounded transition-colors cursor-pointer" data-id="element-422">
                      <input type="checkbox" checked={isSelected} onChange={() => toggleProduct(product)} className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded" data-id="element-423" />
                      <span className="ml-3 text-sm font-medium text-gray-900" data-id="element-424">
                        {product.name}
                      </span>
                    </label>

                    {isSelected && bundleProduct && <div className="ml-7 pl-3 border-l-2 border-gray-200" data-id="element-425">
                        <Toggle label="Require size selection at checkout" checked={bundleProduct.requireSizeSelection} onChange={checked => updateProductSizeRequirement(product.id, checked)} data-id="element-426" />
                      </div>}
                  </div>;
          })}
            </div>}
        </div>

        {formData.products.length > 0 && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-427">
            <h4 className="text-sm font-medium text-blue-900 mb-2" data-id="element-428">
              Bundle Summary
            </h4>
            <ul className="text-sm text-blue-800 space-y-1" data-id="element-429">
              {formData.products.map(p => <li key={p.productId} data-id="element-430">
                  • {p.productName}{' '}
                  {p.requireSizeSelection && '(size required)'}
                </li>)}
            </ul>
          </div>}
      </div>
    </Modal>;
}